/**
 * @fileoverview This file contains the VersionManagementService, responsible for managing
 * version updates and service worker interactions.
 */

import {Workbox, WorkboxLifecycleWaitingEvent} from 'workbox-window';

import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {IServiceWorkerVersion} from '../data';
import {ServiceWorkerChannelService} from './service-worker/service-worker-channel.service';
import {ServiceWorkerManagmentService} from './service-worker/service-worker-managment.service';

@Injectable({
	providedIn: 'root',
})
export class VersionManagementService {
	/* ------------------------------------ Services and Providers ------------------------------------ */
	private wb: Workbox | null = null;
	private readonly serviceWorkerManagementService = inject(ServiceWorkerManagmentService);
	public readonly swChannelService = inject(ServiceWorkerChannelService);
	private readonly translateService = inject(TranslateService);

	/* ---------------------------------------- Initialization --------------------------------------- */
	/**
	 * Initializes the service with a Workbox instance.
	 * @param workbox - The Workbox instance to use.
	 * @throws Error if Workbox instance is not set.
	 */
	public initialize(workbox: Workbox): void {
		this.wb = workbox;
		if (!this.wb) throw new Error('[VersionManagementService] Workbox instance is not set');
		this.setupEventListeners();
	}

	/* -------------------------------------- Event Listeners ---------------------------------------- */
	/**
	 * Sets up event listeners for service worker messages and lifecycle events.
	 */
	private setupEventListeners(): void {
		if (!this.wb) return;
		this.swChannelService.onEvent('newVersionAvailable').subscribe(event => this.handleVersionUpdate(event.payload));
		this.setupWorkboxEventListeners();
		this.serviceWorkerManagementService.initialize(this.wb);
	}

	/**
	 * Sets up listeners for Workbox lifecycle events.
	 */
	private setupWorkboxEventListeners(): void {
		this.wb?.addEventListener('waiting', (event: WorkboxLifecycleWaitingEvent) => {
			this.log('A new Service Worker is waiting to activate', event);
			this.serviceWorkerManagementService.reloadPage();
		});

		this.wb?.addEventListener('controlling', event => {
			this.log('Service Worker is controlling the page', event);
			this.serviceWorkerManagementService.reloadPage();
		});
	}

	/* ------------------------------------ Update Management --------------------------------------- */
	/**
	 * Checks for available updates.
	 * @returns A promise that resolves to a boolean indicating if an update is available.
	 */
	public async checkForUpdates(): Promise<boolean> {
		if (!this.wb) return false;

		try {
			await this.wb.update();
			const registration = await navigator.serviceWorker.getRegistration();
			return !!registration?.waiting;
		} catch (error) {
			console.error('Error checking for updates:', error);
			return false;
		}
	}

	/**
	 * Handles a version update.
	 * @param versionInfo - Information about the new version.
	 */
	public handleVersionUpdate(versionInfo: IServiceWorkerVersion): void {
		this.log('New version is available:', versionInfo);
		const message = this.translateService.instant('version.alert-user');
		alert(message);
		this.serviceWorkerManagementService.swChannelService.newSendMessageToSw({
			action: 'skipWaiting',
			params: [],
		});
	}

	/* ------------------------------------------ Utility -------------------------------------------- */
	/**
	 * Logs a message with the service name prefix.
	 * @param args - Arguments to log.
	 */
	private log(...args: any[]): void {
		console.log('[VersionManagementService]', ...args);
	}
}
